import { useSelector, useDispatch } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';

import { useEffect, useRef, Suspense } from 'react';

import { useStepLoading, pageComponents, stepComponents } from '../hooks/useStepLoading';



import {
  decrementStep,
  setApplyDetails,
  setBenefits,
  setBenefitsLoading,
  setCurrentStep,
  setEducationalInstitutions,
  setHealthFundLoading,
  setHealthFunds,
  setProductDetails,
  setStates,
  setTestMode,
  setUtmData,
  updateFormData,
  updateSalesReqBody,
} from '../features/FormRegistration';
import { CoverIsFor } from '../types/types';
import axiosRequest from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { calculateAge } from '../utils/helpers';
import axios from 'axios';



function StartPage() {
  const {
    salesReqBody,
    currentStep,
    productDetails,
    applyDetails,
    formData,
    utmData,
  } = useSelector((state: any) => state.formRegistration);

  // Use our custom hook
  const { StepComponent, LoadingFallback } = useStepLoading(currentStep);
  const { ProductDetails, ApplyPage } = pageComponents;

  const previousStepRef = useRef(currentStep);

  const variants = {
    initial: (isMovingForward: boolean) => ({
      opacity: 0,
      x: isMovingForward ? 100 : -100,
    }),
    animate: { opacity: 1, x: 0 },
    exit: (isMovingForward: boolean) => ({
      opacity: 0,
      x: isMovingForward ? -100 : 100,
    }),
  };

  const isMovingForward = currentStep > previousStepRef.current;

  useEffect(() => {
    previousStepRef.current = currentStep;
  }, [currentStep]);

  const dispatch = useDispatch();

  const queryParams = new URLSearchParams(window.location.search);

  const searchCoverIsFor = queryParams.get('coverType');
  const isTestMode = queryParams.get('test');

  useEffect(() => {
    const savedSalesReqBody = localStorage.getItem('salesReqBody');
    const savedFormData = localStorage.getItem('formData');
    const currentStep = localStorage.getItem('currentStep');

    if (savedSalesReqBody) {
      dispatch(updateSalesReqBody(JSON.parse(savedSalesReqBody)));
    }
    if (savedFormData) {
      dispatch(updateFormData(JSON.parse(savedFormData)));
    }
    if (currentStep) {
      dispatch(setCurrentStep(Number(currentStep)));
    }
  }, []);

  useEffect(() => {
    if (formData) {
      localStorage.setItem('formData', JSON.stringify(formData));
    }
  }, [formData]);

  useEffect(() => {
    if (currentStep) {
      localStorage.setItem('currentStep', currentStep);
    }
  }, [currentStep]);

  useEffect(() => {
    {
      if (salesReqBody) {
        localStorage.setItem('salesReqBody', JSON.stringify(salesReqBody));

        if (isTestMode === 'true') {
          localStorage.setItem('salesReqBody', JSON.stringify(salesReqBody));
        } else {
          const updatedSalesReqBody = {
            ...salesReqBody,
            payment: {
              payment_method: '',
              payment_date: '',
              payment_period: '',
              upfront_payment: false,
              cc_name: '',
              cc_number: '',
              cc_expiry_month: '',
              cc_expiry_year: 0,
              cc_cvv: '',
              cc_type: 'Visa',
              cc_payment_token: '',
              bank_name: '',
              account_name: '',
              bsb_number: '',
              account_number: '',
              allowDirectDebit: true,
            },
          };
          localStorage.setItem(
            'salesReqBody',
            JSON.stringify(updatedSalesReqBody),
          );
        }
      }
    }
  }, [salesReqBody]);

  useEffect(() => {
    if (isTestMode === 'true') {
      dispatch(setTestMode(true));
    }
  }, [isTestMode]);

  useEffect(() => {
    dispatch(setBenefitsLoading(true));

    axiosRequest
      .post('/benefitsList', {
        order: 'web_form',
      })
      .then((res) => {
        const benefitsObj = Object.values(res.data.data);

        dispatch(setBenefits(benefitsObj));
      })
      .catch((err) => {
        console.log(err);
        toast.error('Error fetching hospital cover features');
      })
      .finally(() => {
        dispatch(setBenefitsLoading(false));
      });
  }, []);

  useEffect(() => {
    dispatch(setHealthFundLoading(true));
    axiosRequest
      .get('/relatedHealthFunds')
      .then((response) => {
        const uniqueFunds = response.data.data.map((item: any) => ({
          value: `${item.related_fund_id.toString()}.${item.health_fund_id.toString()}`,
          label: item.drop_down_display_name,
          health_fund_code: item.health_fund_code,
        }));

        dispatch(setHealthFunds(uniqueFunds));
      })
      .catch((err) => {
        console.log(err);
        toast.error('Server error, please try again later');
      })
      .finally(() => {
        dispatch(setHealthFundLoading(false));
      });
  }, []);

  // get user ip address
  useEffect(() => {
    const getIpAddress = async () => {
      try {
        const response = await axios.get('https://api.ipify.org?format=json');
        const ipAddress = response.data.ip;
        dispatch(
          setUtmData({
            ...utmData,
            ip_address: ipAddress,
          }),
        );
      } catch (error) {
        console.error('Error getting ip address', error);
      }
    };
    getIpAddress();
  }, []);

  useEffect(() => {
    switch (searchCoverIsFor) {
      case 'single':
        dispatch(
          updateFormData({ ...formData, coverIsFor: CoverIsFor.Single }),
        );
        dispatch(setCurrentStep(1));
        break;
      case 'couple':
        dispatch(
          updateFormData({ ...formData, coverIsFor: CoverIsFor.Couple }),
        );
        dispatch(setCurrentStep(1));

        break;
      case 'family':
        dispatch(
          updateFormData({ ...formData, coverIsFor: CoverIsFor.Family }),
        );
        dispatch(setCurrentStep(1));

        break;
      case 'singleParentFamily':
        dispatch(
          updateFormData({
            ...formData,
            coverIsFor: CoverIsFor.SingleParentFamily,
          }),
        );
        dispatch(setCurrentStep(1));

        break;
      default:
        break;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('coverType'); // Adjust the parameter name if needed
    window.history.replaceState({}, document.title, url);
  }, [searchCoverIsFor]);

  useEffect(() => {
    axiosRequest
      .get('/states')
      .then((res) => {
        dispatch(setStates(res.data.data));
      })
      .catch((err) => {
        console.log(err);
        toast.error('Server error, please try again later');
      });
  }, []);

  useEffect(() => {
    axiosRequest
      .get('/educationalInstitutions')
      .then((res) => {
        dispatch(
          setEducationalInstitutions(
            res.data.data.map((item: any) => ({
              value: item.institution_code,
              label: item.institution_name,
            })),
          ),
        );
      })
      .catch((err) => {
        console.log(err);
        toast.error('Server error, please try again later');
      });
  }, []);

  useEffect(() => {
    window.history.pushState(
      { preventBack: true },
      document.title,
      location.href,
    );

    const handlePopState = (event: PopStateEvent) => {
      if (event.state && event.state.preventBack) {
        window.history.go(1);
      }

      if (applyDetails) {
        dispatch(setApplyDetails(null));
        return;
      }
      if (productDetails) {
        dispatch(setProductDetails(null));
        return;
      }
      dispatch(decrementStep());
    };

    // Add popstate event listener
    window.addEventListener('popstate', handlePopState);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [applyDetails, productDetails]);

  useEffect(() => {
    if (formData && formData.dateOfBirth && formData.dateOfBirth[0]) {
      // const [day, month, year] = formData.dateOfBirth[0].split('/');
      const dob = formData.dateOfBirth[0].replace(/\//g, '-');
      const age = calculateAge(formData.dateOfBirth[0]);

      dispatch(
        updateSalesReqBody({
          ...salesReqBody,
          customer: {
            ...salesReqBody.customer,
            // dob: `${day}/${month}/${year}`.replace(/\//g, '-'),
            dob,
            age,
          },
        }),
      );
    }
  }, [formData.dateOfBirth?.[0]]);

  useEffect(() => {
    if (formData && formData.dateOfBirth && formData.dateOfBirth[1]) {
      const dob = formData.dateOfBirth[1].replace(/\//g, '-');
      const age = calculateAge(formData.dateOfBirth[1]);
      dispatch(
        updateSalesReqBody({
          ...salesReqBody,
          partner: {
            ...salesReqBody.partner,
            dob,
            age,
          },
        }),
      );
    }
  }, [formData.dateOfBirth?.[1]]);

  useEffect(() => {
    if (!formData.dependantDateOfBirth) return;
    dispatch(
      updateSalesReqBody({
        ...salesReqBody,
        dependants: salesReqBody?.dependants?.map(
          (dependant: any, index: number) => {
            if (formData.dependantDateOfBirth[index]) {
              const dob = formData.dependantDateOfBirth[index].replace(
                /\//g,
                '-',
              );
              const age = calculateAge(formData.dependantDateOfBirth[index]);

              return {
                ...dependant,
                dob,
                age,
              };
            }
            return dependant;
          },
        ),
      }),
    );
  }, [formData.dependantDateOfBirth]);


  useEffect(() => {
    if (isTestMode === 'true') {
      console.log('sales req body updated');
      console.log(salesReqBody);
    }
  }, [salesReqBody, isTestMode]);

  // Preload next step when the current step is loaded

  if (applyDetails) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <ApplyPage />
      </Suspense>
    );
  }
  if (productDetails) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <ProductDetails />
      </Suspense>
    );
  }
  return (
    <>
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep} // Ensures unique animations for each step
          custom={isMovingForward} // Pass the direction as a custom prop
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.3 }} // Controls the speed of animations
        >
          <Suspense fallback={<LoadingFallback />}>
            <StepComponent />
          </Suspense>
        </motion.div>
        {/* Back button */}

      </AnimatePresence>

    </>

  );
}

export default StartPage;
